import ReactECharts from 'echarts-for-react';
import globalUseStyles from '../../../../styles/globalCustomStyles';

import { Card, Typography } from '@mui/material';
import { useRecordContext, useTranslate } from 'react-admin';
import InfoTooltipIcon from "../../../../assets/InfoTooltipIcon";
import ChartLoading from '../../../aegeon/charts/ChartLoading';
import ChartNoData from '../../../aegeon/charts/ChartNoData';
import { getObjectiveYear } from '../../../aegeon/compute_performances';
import CustomTooltip from '../../../aegeon/CustomTooltip';
import { is_value_defined } from '../../../aegeon/helper';
import useQueryProvider from '../../../aegeon/query/useQueryProvider';

const SiteEnergeticPerformanceCRREMObjective = (props) => {
  const globalClasses = globalUseStyles();
  const t = useTranslate();
  const { type, isGraph, data, isLoading, year, usage, colors } = props;
  const record = useRecordContext(props);
  const siteId = record?.id;

  let { data: crremObjectivesTable } = useQueryProvider(
    'sites',
    {
      query_name: "getCrremObjectivesTable",
      id: siteId,
      params: {
        type: usage
      },
    }
  );

  const energy_year = getObjectiveYear("CRREM_PCS", data, crremObjectivesTable);
  const ghg_year = getObjectiveYear("CRREM_GHG", data, crremObjectivesTable);

  const title = t('sites.energetic_performance.' + type + '.title');
  const subtitle = t('sites.energetic_performance.' + type + '.subtitle');
  const tooltip_text = t('sites.energetic_performance.' + type + '.tooltip_text');

  const crrem_pcs = data?.performances?.find((p) => p.type === "CRREM_PCS");

  const energyObjectives = Object.entries(crremObjectivesTable || {})
    .filter(([key]) => key.startsWith('energy_'))
    .map(([key, value]) => {
      const year = key.replace('energy_', '');
      return {
        year: parseInt(year),
        value: Math.round(value * 100) / 100,
      };
    }).filter(obj => obj.year >= year);

  const ghgObjectives = Object.entries(crremObjectivesTable || {})
    .filter(([key]) => key.startsWith('ghg_'))
    .map(([key, value]) => {
      const year = key.replace('ghg_', '');
      return {
        year: parseInt(year),
        value: Math.round(value * 100) / 100,
      };
    }).filter(obj => obj.year >= year);

  let yAxis = [
    {
      type: 'value',
      name: 'Energie (kWh EF PCS/m²)',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: {
        fontSize: 10,
      },
      axisLabel: {
        color: colors["energy"],
      },
    },
    {
      type: 'value',
      name: 'Emissions de GES (kgeqCO2/m²)',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: {
        fontSize: 10,
      },
      axisLabel: {
        color: colors["ghg"],
      },
    }
  ]

  const perf_energy = Math.round(data?.performances?.find((p) => p.type === "CRREM_PCS")?.value * 100) / 100;
  const data_energy_consumption = energyObjectives?.map(obj => [obj.year, perf_energy]);
  const data_energy_objectives = energyObjectives?.map(obj => [obj.year, obj.value]);
  const objective_year_index = energyObjectives.findIndex(obj => obj.year === energy_year);

  const perfs_ghg = data?.performances?.find((p) => p.type === "CRREM_GHG")?.values
  const data_ghg_consumption = perfs_ghg?.map(obj => [obj.year, Math.round(obj.value * 100) / 100]);
  const data_ghg_objectives = ghgObjectives?.map(obj => [obj.year, obj.value]);
  const objective_ghg_year_index = ghgObjectives.findIndex(obj => obj.year === ghg_year);
  const objective_ghg_year_value = ghgObjectives.find(obj => obj.year === ghg_year)?.value;

  const options = {
    tooltip: {
      trigger: 'axis',
      position: ['10%', '0%'],
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '20%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
    },
    yAxis: yAxis,
    series: [
      {
        name: t('sites.energetic_performance.' + type + '.energy_current'),
        type: 'line',
        data: data_energy_consumption,
        smooth: true,
        itemStyle: {
          color: colors["energy"],
        },
        markPoint: {
          symbolSize: 70,
          data: [{ value: energy_year, xAxis: objective_year_index, yAxis: perf_energy }],
          itemStyle: {
            color: "#71B9A1",
          },
          label: {
            color: "#ffffff",
          },
        },
      },
      {
        name: t('sites.energetic_performance.' + type + '.energy'),
        type: 'line',
        data: data_energy_objectives,
        smooth: true,
        itemStyle: {
          color: "#fdcc0c",
        },
      },
      {
        name: t('sites.energetic_performance.' + type + '.ghg_current'),
        type: 'line',
        data: data_ghg_consumption,
        smooth: true,
        yAxisIndex: 1,
        itemStyle: {
          color: colors["ghg"],
        },
        markPoint: {
          symbolSize: 70,
          itemStyle: {
            color: "#784a9c",
          },
          data: [{ value: ghg_year, xAxis: objective_ghg_year_index, yAxis: objective_ghg_year_value }],
        },
      },
      {
        name: t('sites.energetic_performance.' + type + '.ghg'),
        type: 'line',
        data: data_ghg_objectives,
        smooth: true,
        yAxisIndex: 1,
        itemStyle: {
          color: "#a274b6",
        }
      },
    ],
    legend: {
      data: [
        t('sites.energetic_performance.' + type + '.energy_current'),
        t('sites.energetic_performance.' + type + '.ghg_current'),
        t('sites.energetic_performance.' + type + '.energy'),
        t('sites.energetic_performance.' + type + '.ghg'),
      ],
      type: 'scroll',
      bottom: 20,
      textStyle: {
        fontSize: 10
      },
    }
  }

  return (
    <Card className={globalClasses.DetailCard}>
      <div >
        <div className={globalClasses.DetailCardTableTitle}>
          <Typography variant="h8">{title}</Typography>
          <CustomTooltip title={tooltip_text} icon={<InfoTooltipIcon />} />
        </div>
        <div className={globalClasses.DetailCardTableSubtitle}>
          {subtitle}
        </div>
      </div>
      <div>
        {isLoading ? <ChartLoading /> :
          !is_value_defined(crrem_pcs?.value)
            ? <ChartNoData /> :
            <>
              <div className={globalClasses.DetailCardGraph} >
                <ReactECharts
                  option={options}
                  notMerge={true}
                  style={{ height: '90%', width: '100%' }}
                />
              </div>
            </>
        }
      </div>
    </Card>
  )
};

export default SiteEnergeticPerformanceCRREMObjective;